interface AstrologyData {
  sign: string;
  element: string;
  planet: string;
  dailyMessage: string;
  compatibility: string[];
  luckyNumbers: number[];
  numerologyLabels?: string[];
  numerologyDescriptions?: string[];
  luckyColors: string[];
  moonPhase: string;
  planetaryInfluences: {
    planet: string;
    influence: string;
    intensity: number;
  }[];
}

interface ZodiacSign {
  name: string;
  element: 'Fire' | 'Earth' | 'Air' | 'Water';
  planet: string;
  dates: string;
  traits: string[];
  compatibility: string[];
}

class AstrologyService {
  private zodiacSigns: ZodiacSign[] = [
    {
      name: 'Áries',
      element: 'Fire',
      planet: 'Marte',
      dates: '21/03 - 19/04',
      traits: ['Corajoso', 'Determinado', 'Líder natural', 'Impulsivo'],
      compatibility: ['Leão', 'Sagitário', 'Gêmeos', 'Aquário']
    },
    {
      name: 'Touro',
      element: 'Earth',
      planet: 'Vênus',
      dates: '20/04 - 20/05',
      traits: ['Estável', 'Prático', 'Determinado', 'Sensual'],
      compatibility: ['Virgem', 'Capricórnio', 'Câncer', 'Peixes']
    },
    {
      name: 'Gêmeos',
      element: 'Air',
      planet: 'Mercúrio',
      dates: '21/05 - 20/06',
      traits: ['Comunicativo', 'Versátil', 'Intelectual', 'Curioso'],
      compatibility: ['Libra', 'Aquário', 'Áries', 'Leão']
    },
    {
      name: 'Câncer',
      element: 'Water',
      planet: 'Lua',
      dates: '21/06 - 22/07',
      traits: ['Emotivo', 'Intuitivo', 'Protetor', 'Sensível'],
      compatibility: ['Escorpião', 'Peixes', 'Touro', 'Virgem']
    },
    {
      name: 'Leão',
      element: 'Fire',
      planet: 'Sol',
      dates: '23/07 - 22/08',
      traits: ['Criativo', 'Generoso', 'Orgulhoso', 'Dramático'],
      compatibility: ['Áries', 'Sagitário', 'Gêmeos', 'Libra']
    },
    {
      name: 'Virgem',
      element: 'Earth',
      planet: 'Mercúrio',
      dates: '23/08 - 22/09',
      traits: ['Analítico', 'Perfeccionista', 'Prático', 'Modesto'],
      compatibility: ['Touro', 'Capricórnio', 'Câncer', 'Escorpião']
    },
    {
      name: 'Libra',
      element: 'Air',
      planet: 'Vênus',
      dates: '23/09 - 22/10',
      traits: ['Equilibrado', 'Diplomático', 'Artístico', 'Indeciso'],
      compatibility: ['Gêmeos', 'Aquário', 'Leão', 'Sagitário']
    },
    {
      name: 'Escorpião',
      element: 'Water',
      planet: 'Plutão',
      dates: '23/10 - 21/11',
      traits: ['Intenso', 'Misterioso', 'Transformador', 'Apaixonado'],
      compatibility: ['Câncer', 'Peixes', 'Virgem', 'Capricórnio']
    },
    {
      name: 'Sagitário',
      element: 'Fire',
      planet: 'Júpiter',
      dates: '22/11 - 21/12',
      traits: ['Aventureiro', 'Filosófico', 'Otimista', 'Livre'],
      compatibility: ['Áries', 'Leão', 'Libra', 'Aquário']
    },
    {
      name: 'Capricórnio',
      element: 'Earth',
      planet: 'Saturno',
      dates: '22/12 - 19/01',
      traits: ['Ambicioso', 'Disciplinado', 'Responsável', 'Tradicional'],
      compatibility: ['Touro', 'Virgem', 'Escorpião', 'Peixes']
    },
    {
      name: 'Aquário',
      element: 'Air',
      planet: 'Urano',
      dates: '20/01 - 18/02',
      traits: ['Inovador', 'Independente', 'Humanitário', 'Excêntrico'],
      compatibility: ['Gêmeos', 'Libra', 'Áries', 'Sagitário']
    },
    {
      name: 'Peixes',
      element: 'Water',
      planet: 'Netuno',
      dates: '19/02 - 20/03',
      traits: ['Intuitivo', 'Compassivo', 'Artístico', 'Sonhador'],
      compatibility: ['Câncer', 'Escorpião', 'Touro', 'Capricórnio']
    }
  ];

  private dailyMessages = {
    Fire: [
      'Sua energia está em alta hoje. Use essa força para iniciar novos projetos.',
      'O fogo interior queima forte. Confie em sua paixão para guiá-lo.',
      'Momento ideal para tomar iniciativas e liderar mudanças positivas.',
      'Sua criatividade está em ebulição. Deixe-a fluir livremente.',
      'A coragem será sua maior aliada hoje. Não tema os desafios.'
    ],
    Earth: [
      'Mantenha os pés no chão e confie em sua praticidade natural.',
      'Hoje é um dia para construir bases sólidas para o futuro.',
      'Sua perseverança será recompensada. Continue firme em seus objetivos.',
      'A natureza tem mensagens especiais para você hoje. Conecte-se com ela.',
      'Estabilidade e segurança estão ao seu alcance. Trabalhe com paciência.'
    ],
    Air: [
      'Sua mente está clara e receptiva a novas ideias hoje.',
      'Comunicação será a chave para resolver questões pendentes.',
      'Momento ideal para aprender algo novo ou compartilhar conhecimento.',
      'Os ventos da mudança sopram a seu favor. Adapte-se com flexibilidade.',
      'Sua curiosidade natural abrirá portas inesperadas hoje.'
    ],
    Water: [
      'Confie em sua intuição - ela está especialmente aguçada hoje.',
      'As emoções fluem como marés. Permita-se sentir profundamente.',
      'Sua sensibilidade é um dom. Use-a para ajudar outros ao seu redor.',
      'Momento propício para cura emocional e renovação espiritual.',
      'As águas da sabedoria interior estão cristalinas hoje.'
    ]
  };

  private moonPhases = ['Nova', 'Crescente', 'Quarto Crescente', 'Gibosa Crescente', 'Cheia', 'Gibosa Minguante', 'Quarto Minguante', 'Minguante'];

  getSignByBirthDate(birthDate: Date): ZodiacSign | null {
    const month = birthDate.getMonth() + 1;
    const day = birthDate.getDate();

    // Lógica simplificada para determinar o signo
    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return this.zodiacSigns[0]; // Áries
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return this.zodiacSigns[1]; // Touro
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return this.zodiacSigns[2]; // Gêmeos
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return this.zodiacSigns[3]; // Câncer
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return this.zodiacSigns[4]; // Leão
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return this.zodiacSigns[5]; // Virgem
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return this.zodiacSigns[6]; // Libra
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return this.zodiacSigns[7]; // Escorpião
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return this.zodiacSigns[8]; // Sagitário
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return this.zodiacSigns[9]; // Capricórnio
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return this.zodiacSigns[10]; // Aquário
    if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return this.zodiacSigns[11]; // Peixes

    return null;
  }

  // Determinístico: gera 6 números entre 1..60 baseados em data + mapa
  generateLuckyNumbersDeterministic(seedParts: { birthDate: Date; birthTime?: Date; signIndex: number; ascIndex: number; today: Date }): number[] {
    const toInt = (d: Date) => parseInt(`${d.getFullYear()}${(d.getMonth()+1).toString().padStart(2,'0')}${d.getDate().toString().padStart(2,'0')}`);
    const base = toInt(seedParts.birthDate) + (seedParts.birthTime ? (seedParts.birthTime.getHours()*100 + seedParts.birthTime.getMinutes()) : 0);
    const dayCode = toInt(seedParts.today);
    let hash = base * 131 + dayCode * 17 + seedParts.signIndex * 37 + seedParts.ascIndex * 53;
    const primes = [2,3,5,7,11,13];
    const nums: number[] = [];
    for (let i = 0; i < 6; i++) {
      hash = (hash * 1664525 + 1013904223) >>> 0; // LCG determinístico
      let n = (hash % 60) + 1;
      // evitar duplicados de forma determinística
      while (nums.includes(n)) {
        n = ((n + primes[i]) % 60) + 1;
      }
      nums.push(n);
    }
    return nums.sort((a,b) => a-b);
  }

  getLuckyColors(element: string): string[] {
    const colorMap = {
      Fire: ['Vermelho', 'Laranja', 'Dourado'],
      Earth: ['Verde', 'Marrom', 'Bege'],
      Air: ['Azul', 'Amarelo', 'Branco'],
      Water: ['Azul-marinho', 'Prata', 'Violeta']
    };
    return colorMap[element as keyof typeof colorMap] || ['Branco', 'Prata'];
  }

  getCurrentMoonPhase(): string {
    const d = new Date();
    const synodic = 29.53058867;
    const known = Date.UTC(2000, 0, 6, 18, 14, 0);
    const now = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const days = (now - known) / 86400000;
    const phase = ((days % synodic) + synodic) % synodic;
    const idx = Math.floor((phase / synodic) * 8) % 8;
    return this.moonPhases[idx];
  }

  getPlanetaryInfluences(): { planet: string; influence: string; intensity: number }[] {
    const influences = [
      { planet: 'Mercúrio', influence: 'Comunicação e aprendizado', intensity: Math.random() * 10 },
      { planet: 'Vênus', influence: 'Amor e relacionamentos', intensity: Math.random() * 10 },
      { planet: 'Marte', influence: 'Energia e ação', intensity: Math.random() * 10 },
      { planet: 'Júpiter', influence: 'Expansão e oportunidades', intensity: Math.random() * 10 },
      { planet: 'Saturno', influence: 'Disciplina e responsabilidade', intensity: Math.random() * 10 }
    ];

    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(),0,0).getTime())/86400000);
    const det = influences.map((inf, idx) => {
      const h = inf.planet.split('').reduce((acc,c)=>acc + c.charCodeAt(0), 0);
      const intensity = ((h + dayOfYear + idx*13) % 10);
      return { ...inf, intensity };
    });
    return det.sort((a,b)=>b.intensity - a.intensity).slice(0,3);
  }

  async getAstrologyDataFromProfile(profile: { birth_date: Date; birth_time?: Date; birth_latitude?: number; birth_longitude?: number; zodiac_sign?: string; name?: string }, chart?: { ascendant?: string }): Promise<AstrologyData> {
    await new Promise(resolve => setTimeout(resolve, 200));

    const birthDate = profile.birth_date;
    const sign = profile.zodiac_sign ? this.zodiacSigns.find(z => z.name === profile.zodiac_sign) || this.getSignByBirthDate(birthDate) || this.zodiacSigns[4] : (this.getSignByBirthDate(birthDate) || this.zodiacSigns[4]);

    const elementMessages = this.dailyMessages[sign.element];
    const signOrder = ['Áries','Touro','Gêmeos','Câncer','Leão','Virgem','Libra','Escorpião','Sagitário','Capricórnio','Aquário','Peixes'];
    const signIndex = signOrder.indexOf(sign.name);
    const ascIndex = chart && chart.ascendant ? signOrder.indexOf(chart.ascendant) : ((profile.birth_time ? profile.birth_time.getHours() : 12) + (profile.birth_longitude ? Math.round(profile.birth_longitude/30) : 0)) % 12;
    const dayOfYear = Math.floor((new Date().getTime() - new Date(new Date().getFullYear(),0,0).getTime())/86400000);
    const msgIndex = (dayOfYear + signIndex + ascIndex) % elementMessages.length;
    const dailyMessage = elementMessages[msgIndex];

    const offset = profile.birth_longitude ? Math.round(profile.birth_longitude / 30) : 0;

    const today = new Date();

    // Numerologia real se tiver nome e data de nascimento
    let luckyNumbers: number[];
    let numerologyLabels: string[] | undefined;
    let numerologyDescriptions: string[] | undefined;
    if (profile.name && profile.name.trim().length > 1) {
      const { calculateNumerology } = await import('./numerology');
      const result = calculateNumerology(profile.name, birthDate, today);
      luckyNumbers = result.numbers;
      numerologyLabels = result.labels;
      numerologyDescriptions = result.descriptions;
    } else {
      luckyNumbers = this.generateLuckyNumbersDeterministic({ birthDate, birthTime: profile.birth_time, signIndex, ascIndex, today });
    }

    const colors = this.getLuckyColors(sign.element);
    const colorIndex = (dayOfYear + signIndex + ascIndex) % colors.length;
    const luckyColors = [colors[colorIndex]];

    return {
      sign: sign.name,
      element: sign.element === 'Fire' ? 'Fogo' : sign.element === 'Earth' ? 'Terra' : sign.element === 'Air' ? 'Ar' : 'Água',
      planet: sign.planet,
      dailyMessage,
      compatibility: sign.compatibility,
      luckyNumbers,
      numerologyLabels,
      numerologyDescriptions,
      luckyColors,
      moonPhase: this.getCurrentMoonPhase(),
      planetaryInfluences: this.getPlanetaryInfluences()
    };
  }

  async getDailyHoroscope(sign: string): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 100));
    const horoscopes = [
      `Hoje é um dia especial para ${sign}. As energias cósmicas estão alinhadas a seu favor.`,
      `${sign}, prepare-se para surpresas positivas. O universo conspira a seu favor.`,
      `A intuição de ${sign} está aguçada hoje. Confie nos sinais que receber.`,
      `${sign}, momento ideal para focar em seus objetivos pessoais e espirituais.`,
      `As estrelas indicam um dia de crescimento e descobertas para ${sign}.`
    ];
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(),0,0).getTime())/86400000);
    const h = sign.split('').reduce((acc,c)=>acc + c.charCodeAt(0), 0);
    const idx = (dayOfYear + h) % horoscopes.length;
    return horoscopes[idx];
  }
}

export const astrologyService = new AstrologyService();
export type { AstrologyData, ZodiacSign };